/**
 * React Query hooks for User Event Dashboards.
 *
 * Card data (run-sql results) intentionally bypass cache: each card
 * re-runs its SQL on every mount/refetch, so refresh = fresh data.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../../config';

export type RenderType = 'bar_chart' | 'line_chart' | 'pie_chart' | 'big_number' | 'table';

export type Dashboard = {
  id: string;
  title: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DashboardListItem = Dashboard & { cardCount: number };

export type DashboardCard = {
  id: string;
  dashboardId: string;
  title: string;
  sql: string;
  renderType: RenderType;
  xKey: string | null;
  yKey: string | null;
  nameKey: string | null;
  layoutX: number;
  layoutY: number;
  layoutW: number;
  layoutH: number;
  createdAt: string;
  updatedAt: string;
};

export type RunSqlResult =
  | {
      ok: true;
      data: Record<string, unknown>[];
      rowCount: number;
      truncated: boolean;
      durationMs: number;
    }
  | { ok: false; error: string };

const base = `${API_URL}/superadmin/user-events/dashboards`;

const jsonRequest = async <T>(
  path: string,
  init?: Omit<RequestInit, 'body'> & { body?: unknown }
): Promise<T> => {
  const { body, headers, ...rest } = init ?? {};
  const res = await fetch(path, {
    credentials: 'include',
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || (payload && payload.ok === false)) {
    throw new Error((payload && payload.error) || `Request failed (${res.status})`);
  }
  return payload as T;
};

const listKey = ['superadmin-user-event-dashboards'] as const;
const detailKey = (id: string) => ['superadmin-user-event-dashboards', id] as const;

export function useDashboardsList() {
  return useQuery({
    queryKey: listKey,
    queryFn: () =>
      jsonRequest<{ ok: true; data: DashboardListItem[] }>(base).then((r) => r.data),
  });
}

export function useDashboard(id: string | undefined) {
  return useQuery({
    queryKey: id ? detailKey(id) : ['superadmin-user-event-dashboards', '__none__'],
    enabled: !!id,
    queryFn: () =>
      jsonRequest<{
        ok: true;
        data: { dashboard: Dashboard; cards: DashboardCard[] };
      }>(`${base}/${id}`).then((r) => r.data),
  });
}

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string }) =>
      jsonRequest<{ ok: true; data: Dashboard }>(base, {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey }).catch(() => {});
    },
  });
}

export function useRenameDashboard(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string }) =>
      jsonRequest<{ ok: true; data: Dashboard }>(`${base}/${id}`, {
        method: 'PATCH',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey }).catch(() => {});
      qc.invalidateQueries({ queryKey: detailKey(id) }).catch(() => {});
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      jsonRequest<{ ok: true }>(`${base}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey }).catch(() => {});
    },
  });
}

export type AddCardInput = {
  title: string;
  sql: string;
  renderType: RenderType;
  xKey?: string | null;
  yKey?: string | null;
  nameKey?: string | null;
  layoutX?: number;
  layoutY?: number;
  layoutW?: number;
  layoutH?: number;
};

export function useAddDashboardCard(dashboardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCardInput) =>
      jsonRequest<{ ok: true; data: DashboardCard }>(`${base}/${dashboardId}/cards`, {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(dashboardId) }).catch(() => {});
      qc.invalidateQueries({ queryKey: listKey }).catch(() => {});
    },
  });
}

export type UpdateCardInput = Partial<AddCardInput>;

export function useUpdateDashboardCard(dashboardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { cardId: string; patch: UpdateCardInput }) =>
      jsonRequest<{ ok: true; data: DashboardCard }>(
        `${base}/${dashboardId}/cards/${input.cardId}`,
        { method: 'PATCH', body: input.patch }
      ).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(dashboardId) }).catch(() => {});
    },
  });
}

export function useDeleteDashboardCard(dashboardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) =>
      jsonRequest<{ ok: true }>(`${base}/${dashboardId}/cards/${cardId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(dashboardId) }).catch(() => {});
      qc.invalidateQueries({ queryKey: listKey }).catch(() => {});
    },
  });
}

export type LayoutItem = { id: string; x: number; y: number; w: number; h: number };

export function useUpdateLayout(dashboardId: string) {
  return useMutation({
    mutationFn: (items: LayoutItem[]) =>
      jsonRequest<{ ok: true }>(`${base}/${dashboardId}/cards/layout`, {
        method: 'PATCH',
        body: { items },
      }),
  });
}

/**
 * Run a card's SQL fresh. `staleTime: 0` + `gcTime: 0` ensures a fresh
 * fetch on every mount — no cache reuse across visits.
 */
export function useRunCardQuery(sql: string, enabled = true) {
  return useQuery({
    queryKey: ['superadmin-user-event-dashboards-run-sql', sql],
    enabled: enabled && sql.trim().length > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<RunSqlResult> => {
      const res = await fetch(`${base}/run-sql`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sql }),
      });
      const payload = (await res.json().catch(() => null)) as RunSqlResult | null;
      if (payload && typeof payload === 'object' && 'ok' in payload) {
        return payload;
      }
      return { ok: false, error: `Request failed (${res.status})` };
    },
  });
}
